import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  MarkEmailRead as MarkAllReadIcon,
  DeleteSweep as ClearAllIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';
import { getNotificationRoute } from '../utils/notificationRoutes';
import NotificationList from '../components/NotificationList';
import NotificationItem from '../components/NotificationItem';
import PageShell from '../components/bento/PageShell';
import BentoCard from '../components/bento/BentoCard';
import BentoTabs from '../components/bento/BentoTabs';
import KpiTile from '../components/bento/KpiTile';
import EmptyState from '../components/bento/EmptyState';
import {
  PURPLE_DARK, BG_PURPLE_GRADIENT, BORDER_PURPLE, KPI_SHADOW_PURPLE,
  SUCCESS_FG, ERROR_FG, BG_TINT, DIVIDER, TEXT_DARK, TEXT_SECONDARY, TEXT_SUBTLE, BG_PURPLE_TINT,
} from '../components/bento/bentoTokens';

const LiveBadge = ({ connected }) => (
  <Box
    sx={{
      display: 'flex', alignItems: 'center', gap: '7px',
      background: connected ? 'linear-gradient(135deg,#166534,#15803d)' : '#8b8696',
      color: '#fff', fontSize: 12.5, fontWeight: 700, padding: '8px 14px', borderRadius: '999px',
      boxShadow: connected ? '0 4px 14px -4px rgba(22,101,52,.5)' : 'none',
    }}
  >
    <Box
      component="span"
      sx={{
        width: 8, height: 8, borderRadius: '50%', background: connected ? '#4ade80' : '#e5e2ea',
        display: 'inline-block',
        ...(connected && {
          animation: 'livePulse 1.4s infinite',
          '@keyframes livePulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
        }),
      }}
    />
    {connected ? 'Live' : 'Offline'}
  </Box>
);

const NotificationPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    notifications,
    unreadCount,
    isConnected,
    isLoading,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    refreshNotifications
  } = useNotifications();

  const [tabValue, setTabValue] = useState(0);
  const [filteredNotifications, setFilteredNotifications] = useState([]);

  useEffect(() => {
    switch (tabValue) {
      case 0:
        setFilteredNotifications(notifications);
        break;
      case 1:
        setFilteredNotifications(notifications.filter(n => !n.isRead));
        break;
      case 2:
        setFilteredNotifications(notifications.filter(n => n.type === 'package_update'));
        break;
      default:
        setFilteredNotifications(notifications);
    }
  }, [notifications, tabValue]);

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    const route = getNotificationRoute(notification);
    if (route) navigate(route);
  };

  const handleDeleteNotification = (notificationId) => {
    console.log('Delete notification:', notificationId);
  };

  const withdrawalsCount = notifications.filter(n => n.type === 'withdrawal').length;
  const topupsCount = notifications.filter(n => n.type === 'wallet_topup_request').length;

  return (
    <PageShell
      title="Notifications"
      subtitle="Stay updated with your package deliveries and shipments"
      sx={{ maxWidth: 1280 }}
      actions={
        <Box display="flex" alignItems="center" gap="10px">
          <LiveBadge connected={isConnected} />
          <Tooltip title="Refresh notifications">
            <IconButton
              onClick={refreshNotifications}
              disabled={isLoading}
              sx={{ width: 38, height: 38, borderRadius: '11px', border: `1px solid ${DIVIDER}`, '&:hover': { background: BG_PURPLE_TINT } }}
            >
              <RefreshIcon sx={{ fontSize: 16, color: TEXT_SUBTLE }} />
            </IconButton>
          </Tooltip>
        </Box>
      }
    >
      {/* KPI strip */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(4,1fr)' }, gap: '14px', mb: '22px' }}>
        <KpiTile
          label="Total"
          value={notifications.length}
          caption="all notifications"
          valueColor={PURPLE_DARK}
          sx={{ background: BG_PURPLE_GRADIENT, border: `1px solid ${BORDER_PURPLE}`, boxShadow: KPI_SHADOW_PURPLE }}
        />
        <KpiTile
          label="Unread"
          value={unreadCount}
          caption={unreadCount === 0 ? 'all caught up ✓' : 'needs review'}
        />
        <KpiTile
          label="Withdrawals"
          value={withdrawalsCount}
          caption="pending review"
          valueColor={ERROR_FG}
        />
        <KpiTile
          label="Top-ups"
          value={topupsCount}
          caption="deposits received"
          valueColor={SUCCESS_FG}
        />
      </Box>

      {/* Main card */}
      <BentoCard bodySx={{ p: 0 }}>
        {/* Tab bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '16px 22px', borderBottom: `1px solid ${DIVIDER}`, background: BG_TINT, flexWrap: 'wrap', gap: '12px' }}>
          <BentoTabs
            tabs={[
              { key: 'all', label: 'All', badge: notifications.length },
              { key: 'unread', label: 'Unread', badge: unreadCount },
              { key: 'packages', label: 'Package Updates' },
            ]}
            value={tabValue}
            onChange={setTabValue}
            sx={isMobile ? { width: '100%' } : undefined}
          />

          <Box display="flex" alignItems="center" gap="8px">
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<MarkAllReadIcon sx={{ fontSize: 15 }} />}
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
              sx={{ borderRadius: '9px', fontSize: 12, fontWeight: 700 }}
            >
              Mark all read
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<ClearAllIcon sx={{ fontSize: 15 }} />}
              onClick={clearNotifications}
              disabled={notifications.length === 0}
              sx={{ borderRadius: '9px', fontSize: 12, fontWeight: 700 }}
            >
              Clear all
            </Button>
          </Box>
        </Box>

        {/* Section header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '16px 22px 10px' }}>
          <Box display="flex" alignItems="center" gap="8px">
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.1em', color: TEXT_SECONDARY, textTransform: 'uppercase' }}>
              Recent Notifications
            </Typography>
            <Box component="span" sx={{ fontFamily: "'Space Grotesk', monospace", fontSize: 12, fontWeight: 700, color: PURPLE_DARK, background: BG_PURPLE_TINT, px: '9px', py: '3px', borderRadius: '999px' }}>
              {filteredNotifications.length}
            </Box>
          </Box>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={refreshNotifications}
              disabled={isLoading}
              sx={{ width: 30, height: 30, borderRadius: '9px', border: `1px solid ${DIVIDER}`, '&:hover': { background: BG_PURPLE_TINT } }}
            >
              <RefreshIcon sx={{ fontSize: 13, color: TEXT_SUBTLE }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Desktop rows */}
        {!isMobile && (
          <NotificationList
            notifications={filteredNotifications}
            onNotificationClick={handleNotificationClick}
            onDeleteNotification={handleDeleteNotification}
          />
        )}
      </BentoCard>

      {/* Mobile cards */}
      {isMobile && (
        <Box sx={{ mt: 2 }}>
          {filteredNotifications.length === 0 ? (
            <BentoCard bodySx={{ p: 0 }}>
              <EmptyState
                message={
                  <>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: TEXT_DARK, mb: 0.5 }}>
                      {tabValue === 1 ? 'No unread notifications' : 'No notifications'}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: TEXT_SUBTLE, mb: notifications.length === 0 ? 2 : 0 }}>
                      {tabValue === 1
                        ? "All caught up! You're up to date with your notifications."
                        : "You'll receive notifications when your packages are updated"}
                    </Typography>
                    {notifications.length === 0 && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<RefreshIcon />}
                        onClick={refreshNotifications}
                        disabled={isLoading}
                      >
                        Refresh
                      </Button>
                    )}
                  </>
                }
              />
            </BentoCard>
          ) : (
            <Box>
              {filteredNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={handleNotificationClick}
                  onDelete={handleDeleteNotification}
                  onMarkAsRead={markAsRead}
                  showActions={true}
                  compact={false}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </PageShell>
  );
};

export default NotificationPage;
